<template>
  <div class="api-docs-page">
    <nav class="docs-sidebar" aria-label="接口目录">
      <div class="docs-brand"><i class="el-icon-connection" /><div><strong>在途智运</strong><span>开放接口文档</span></div></div>
      <button type="button" class="docs-nav-item docs-overview" :class="{ 'is-active': !currentDoc }" @click="selectDoc('overview')">接入说明</button>
      <section v-for="group in groups" :key="group.id" class="docs-nav-group">
        <h2>{{ group.title }}</h2>
        <button v-for="doc in group.docs" :key="doc.id" type="button" class="docs-nav-item" :class="{ 'is-active': currentDoc && currentDoc.id === doc.id }" @click="selectDoc(doc.id)">{{ doc.title }}</button>
      </section>
    </nav>
    <article ref="article" class="docs-content">
      <div class="docs-eyebrow">OPEN API <span>V1 · 灵动业务契约</span></div>
      <template v-if="!currentDoc">
        <h1>接入说明</h1>
        <p class="docs-lead">同步运输任务，查询运输动态，接收在途事件。</p>
        <section id="overview-conventions" class="docs-section">
          <h2>协议与约定</h2>
          <p v-for="(note, index) in integrationNotes" :key="index">{{ note }}</p>
        </section>
        <section id="overview-authentication" class="docs-section">
          <h2>公共请求头</h2>
          <p>Bearer Token 与 AppKey 签名认证二选一。接入凭据由管理员开通，请在可信服务端保管。</p>
          <div class="docs-table-wrap"><table><thead><tr><th>请求头</th><th>必选</th><th>说明</th><th>示例</th></tr></thead><tbody><tr v-for="field in authenticationHeaders" :key="field.name"><td><code>{{ field.name }}</code></td><td>{{ field.required }}</td><td>{{ field.description }}</td><td><code>{{ field.example }}</code></td></tr></tbody></table></div>
          <h3>账号登录</h3>
          <pre><code>{{ loginExample }}</code></pre>
          <p>成功响应 data.token 为 API Token，有效期 7 天；同账号再次登录会使旧 Token 立即失效。</p>
          <h3>HMAC-SHA256 签名</h3>
          <p>对以下六行规范字符串签名，行尾不额外换行；使用 AppSecret 计算 HMAC-SHA256，输出 64 位小写十六进制。</p>
          <pre><code>{{ signatureFormat }}</code></pre>
          <p>METHOD 大写；REQUEST_URI 包含部署前缀；查询参数先按 RFC 3986 编码再按键排序，空格使用 %20，字面加号使用 %2B。SHA256_BODY 对最终发送的 UTF-8 表单字节计算，签名后不能再次改写字段顺序、空格或编码。Nonce 允许字母、数字、._~-，最长 128 字符。</p>
        </section>
        <section id="overview-status" class="docs-section">
          <h2>业务响应与状态码</h2>
          <pre><code>{
  "status": 1001,
  "result": null
}</code></pre>
          <div class="docs-table-wrap"><table><thead><tr><th>状态码</th><th>含义</th></tr></thead><tbody><tr v-for="status in statusCodes" :key="status.code"><td><code>{{ status.code }}</code></td><td>{{ status.description }}</td></tr></tbody></table></div>
        </section>
        <section id="overview-callback" class="docs-section">
          <h2>通知接收</h2>
          <p>主账号在系统管理 → 回调配置中保存 HTTPS 公网通知地址。收到通知后先持久化并按业务事件去重，再返回 success。失败投递可能重试，处理程序须允许重复通知。</p>
          <pre><code>HTTP/1.1 200 OK
Content-Type: text/plain; charset=UTF-8

success</code></pre>
          <p>白皮书通知与现有 Webhook 是两种协议：本页八类通知使用表单和 type；补充章节的位置订阅使用 JSON 信封、eventId 和 X-Webhook-* 签名头。</p>
        </section>
        <section id="overview-differences" class="docs-section">
          <h2>当前能力与差异</h2>
          <ul><li>业务字段参照白皮书，认证、错误拦截和接口路径沿用本系统约定。</li><li>创建任务目前需要有效坐标和计划时间；灾害天气开启会明确失败。</li><li>插件当前仅支持单运单轨迹分享，其他类型和时间裁剪未开放。</li><li>逐项支持情况、原文冲突处理和数据限制均在接口正文中说明。</li></ul>
        </section>
      </template>
      <template v-else>
        <h1>{{ currentDoc.title }}</h1>
        <p class="docs-lead">{{ currentDoc.summary }}</p>
        <section id="request-address" class="docs-section">
          <h2>{{ currentDoc.callback ? '通知地址' : '请求地址' }}</h2>
          <div class="docs-endpoint"><span>{{ currentDoc.method }}</span><code>{{ currentDoc.path }}</code></div>
          <p v-if="currentDoc.callback">方向：在途智运 → 客户接收端。使用主账号配置的默认通知地址，通过 type 区分事件。</p>
          <p v-else-if="currentDoc.write">写操作须携带 Idempotency-Key，并使用公共请求头中的任一认证方式。</p>
          <p v-else>使用公共请求头中的任一认证方式，{{ currentDoc.bodyKind === 'json' ? '请求体为 JSON' : currentDoc.method === 'GET' ? '参数放在 URL 查询串中' : '请求体为 UTF-8 表单' }}。</p>
          <p v-if="apiScopes[currentDoc.id]">所需 Scope：<code>{{ apiScopes[currentDoc.id] }}</code>；同时受绑定账号角色及数据范围约束。</p>
        </section>
        <section id="request-parameters" class="docs-section">
          <h2>{{ currentDoc.callback ? '通知参数' : '请求参数' }}</h2>
          <div class="docs-table-wrap"><table><thead><tr><th>参数名</th><th>必选</th><th>类型</th><th>层级</th><th>说明</th><th>示例</th></tr></thead><tbody><tr v-for="(field, index) in currentDoc.params" :key="field.name + '-' + index"><td><code>{{ field.name }}</code></td><td :class="{ 'docs-required': field.required === '是' }">{{ field.required }}</td><td>{{ field.type }}</td><td>{{ field.level }}</td><td>{{ field.description }}</td><td><code>{{ field.example || '—' }}</code></td></tr></tbody></table></div>
        </section>
        <section id="request-example" class="docs-section">
          <h2>{{ currentDoc.callback ? '通知示例' : '请求示例' }}</h2>
          <p v-if="hasJsonString">表单中的 JSON 字符串按原始内容序列化后，交给 --data-urlencode 编码；不要先手动编码再重复编码。</p>
          <pre><code>{{ requestExample }}</code></pre>
        </section>
        <section v-if="currentDoc.response" id="response-parameters" class="docs-section">
          <h2>返回参数</h2>
          <div class="docs-table-wrap"><table><thead><tr><th>参数名</th><th>类型</th><th>层级</th><th>说明</th><th>示例</th></tr></thead><tbody><tr v-for="(field, index) in currentDoc.response" :key="field.name + '-' + index"><td><code>{{ field.name }}</code></td><td>{{ field.type }}</td><td>{{ field.level }}</td><td>{{ field.description }}</td><td><code>{{ field.example || '—' }}</code></td></tr></tbody></table></div>
        </section>
        <section id="success-example" class="docs-section">
          <h2>{{ currentDoc.callback ? '接收成功响应' : '成功返回示例' }}</h2>
          <pre><code>{{ successExample }}</code></pre>
          <div v-for="example in currentDoc.extraExamples || []" :key="example.title"><h3>{{ example.title }}</h3><pre><code>{{ example.code }}</code></pre></div>
        </section>
        <section id="failure-example" class="docs-section">
          <h2>{{ currentDoc.callback ? '接收失败示例' : '失败返回示例' }}</h2>
          <pre><code>{{ failureExample }}</code></pre>
          <p v-if="currentDoc.callback">超时、非 2xx、或响应正文不是 success / "success" 均未确认成功，可能触发重试。</p>
        </section>
        <section id="interface-notes" class="docs-section docs-notes">
          <h2>使用说明</h2>
          <ul><li v-for="(note, index) in currentDoc.notes" :key="index">{{ note }}</li></ul>
        </section>
      </template>
      <div class="docs-footer">在途智运开放接口 · 业务字段依据灵动产品白皮书 V1.0.18</div>
    </article>
    <nav class="docs-outline" aria-label="本页目录"><strong>本页目录</strong><a v-for="item in outline" :key="item.id" :href="'#' + item.id" @click.prevent="scrollToSection(item.id)">{{ item.title }}</a></nav>
  </div>
</template>

<script>
import { apiDocs, apiGroups, apiScopes, authenticationHeaders, integrationNotes, statusCodes, buildRequestExample } from './apiDocsData'

export default {
  name: 'ApiDocs',
  data() {
    return {
      apiDocs, apiScopes, authenticationHeaders, integrationNotes, statusCodes,
      loginExample: 'POST /openapi/v1/auth/login\nContent-Type: application/json\n\n{"username":"your-account","password":"your-password"}',
      signatureFormat: 'METHOD\nREQUEST_URI\nNORMALIZED_QUERY\nSHA256_BODY\nX_TIMESTAMP\nX_NONCE'
    }
  },
  computed: {
    groups() { return apiGroups.map(group => ({ ...group, docs: this.apiDocs.filter(doc => doc.group === group.id) })) },
    currentDoc() { return this.apiDocs.find(doc => doc.id === this.$route.query.api) || null },
    hasJsonString() { return this.currentDoc && Object.keys(this.currentDoc.request).some(key => ['dataList', 'rgcdata', 'pluginParams'].includes(key)) },
    requestExample() { return this.currentDoc ? buildRequestExample(this.currentDoc) : '' },
    successExample() { return this.currentDoc && this.currentDoc.callback ? 'HTTP/1.1 200 OK\nContent-Type: text/plain; charset=UTF-8\n\nsuccess' : JSON.stringify(this.currentDoc && this.currentDoc.success, null, 2) },
    failureExample() { return this.currentDoc && this.currentDoc.callback ? 'HTTP/1.1 500 Internal Server Error\nContent-Type: text/plain; charset=UTF-8\n\nfailed' : JSON.stringify(this.currentDoc && this.currentDoc.failure, null, 2) },
    outline() {
      if (!this.currentDoc) return [
        { id: 'overview-conventions', title: '协议与约定' }, { id: 'overview-authentication', title: '公共请求头' },
        { id: 'overview-status', title: '业务响应与状态码' }, { id: 'overview-callback', title: '通知接收' }, { id: 'overview-differences', title: '当前能力与差异' }
      ]
      return [
        { id: 'request-address', title: this.currentDoc.callback ? '通知地址' : '请求地址' },
        { id: 'request-parameters', title: this.currentDoc.callback ? '通知参数' : '请求参数' },
        { id: 'request-example', title: this.currentDoc.callback ? '通知示例' : '请求示例' },
        ...(this.currentDoc.response ? [{ id: 'response-parameters', title: '返回参数' }] : []),
        { id: 'success-example', title: '成功示例' }, { id: 'failure-example', title: '失败示例' }, { id: 'interface-notes', title: '使用说明' }
      ]
    }
  },
  watch: { '$route.query.api'() { this.$nextTick(() => this.$refs.article && this.$refs.article.scrollIntoView({ block: 'start' })) } },
  methods: {
    selectDoc(id) {
      const api = id === 'overview' ? undefined : id
      if (this.$route.query.api === api) return
      this.$router.push({ path: '/api-docs', query: api ? { api } : {} })
    },
    scrollToSection(id) {
      const element = this.$refs.article.querySelector('#' + id)
      if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}
</script>

<style scoped>
.api-docs-page { display: grid; grid-template-columns: 218px minmax(0, 1fr) 132px; gap: 24px; align-items: start; color: #344054; }
.docs-sidebar, .docs-outline { position: sticky; top: 16px; max-height: calc(100vh - 92px); overflow-y: auto; }
.docs-sidebar { padding: 18px 10px 20px 0; }
.docs-brand { display: flex; gap: 12px; align-items: center; padding: 0 14px 22px; color: #1677ff; }
.docs-brand > i { font-size: 27px; }
.docs-brand strong, .docs-brand span { display: block; }
.docs-brand strong { color: #182230; font-size: 18px; letter-spacing: 1px; }
.docs-brand span { margin-top: 4px; color: #98a2b3; font-size: 12px; }
.docs-nav-group { margin-top: 22px; }
.docs-nav-group h2 { margin: 0 14px 8px; color: #475467; font-size: 13px; font-weight: 600; }
.docs-nav-item { display: block; width: 100%; border: 0; padding: 9px 14px; background: transparent; color: #667085; text-align: left; font: inherit; font-size: 13px; line-height: 1.5; border-radius: 5px; cursor: pointer; }
.docs-nav-item:hover { color: #1677ff; background: #eaf1fc; }
.docs-nav-item.is-active { color: #1677ff; background: #e5eefc; font-weight: 600; }
.docs-overview { font-size: 14px; }
.docs-content { min-width: 0; padding: 38px 36px 24px; border: 1px solid #e9edf3; border-radius: 8px; background: #fff; line-height: 1.8; scroll-margin-top: 16px; }
.docs-eyebrow { display: flex; gap: 14px; align-items: center; color: #1677ff; font-size: 12px; font-weight: 600; letter-spacing: 1px; }
.docs-eyebrow span { color: #98a2b3; font-weight: 400; letter-spacing: 0; }
.docs-content h1 { margin: 16px 0 14px; color: #101828; font-size: 28px; font-weight: 600; line-height: 1.4; }
.docs-lead { margin: 0 0 32px; color: #667085; font-size: 15px; }
.docs-section { margin-top: 34px; scroll-margin-top: 20px; }
.docs-section h2 { padding-top: 20px; border-top: 1px solid #eaecf0; margin: 0 0 16px; color: #182230; font-size: 19px; font-weight: 600; }
.docs-section h3 { margin: 24px 0 12px; color: #344054; font-size: 15px; }
.docs-section p, .docs-section li { font-size: 14px; overflow-wrap: anywhere; }
.docs-section p { margin: 12px 0; }
.docs-section ul { padding-left: 20px; }
.docs-section li + li { margin-top: 9px; }
.docs-endpoint { display: flex; gap: 14px; align-items: center; padding: 14px 16px; background: #f8fafc; border: 1px solid #eaecf0; border-radius: 5px; overflow-wrap: anywhere; }
.docs-endpoint span { color: #027a48; font-size: 12px; font-weight: 700; }
.docs-endpoint code { color: #344054; font-size: 13px; }
.docs-table-wrap { max-width: 100%; overflow-x: auto; margin: 18px 0; }
.docs-content table { width: 100%; border-collapse: collapse; font-size: 12px; text-align: left; }
.docs-content th { background: #f9fafb; color: #344054; font-weight: 600; white-space: nowrap; }
.docs-content th, .docs-content td { padding: 11px 12px; border: 1px solid #eaecf0; vertical-align: top; }
.docs-content td { min-width: 40px; overflow-wrap: anywhere; }
.docs-content td:first-child { min-width: 110px; }
.docs-content td:last-child { min-width: 95px; }
.docs-content td code { font-size: 12px; color: #475467; }
.docs-content td.docs-required { color: #e5484d; }
.docs-content pre { max-width: 100%; overflow: auto; padding: 18px 20px; border-radius: 6px; background: #f6f8fb; color: #344054; line-height: 1.7; font-size: 12px; tab-size: 2; }
.docs-content code { font-family: SFMono-Regular, Consolas, 'Liberation Mono', monospace; }
.docs-notes { padding: 0 18px 10px; background: #f5f8ff; border-left: 3px solid #84adff; border-radius: 4px; }
.docs-notes h2 { padding-top: 16px; border: 0; font-size: 16px; }
.docs-outline { display: flex; flex-direction: column; gap: 11px; margin-top: 38px; padding-left: 16px; border-left: 1px solid #dfe4ec; font-size: 12px; }
.docs-outline strong { margin-bottom: 3px; color: #344054; font-size: 13px; }
.docs-outline a { color: #667085; text-decoration: none; }
.docs-outline a:hover { color: #1677ff; }
.docs-footer { margin-top: 42px; padding-top: 20px; border-top: 1px solid #eaecf0; color: #98a2b3; font-size: 12px; }
@media (max-width: 1400px) { .api-docs-page { grid-template-columns: 198px minmax(0, 1fr); gap: 18px; } .docs-outline { display: none; } .docs-content { padding: 30px 26px; } }
</style>
